import { type z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { makeListQuery } from '../../common/list-query/list-query';
import { ConversationStatusSchema } from '../conversation-status/conversation-status';

/**
 * The member's conversations.
 *
 * **Offset-paginated, unlike the message history**, and the difference is not an
 * inconsistency. A member's conversations are bounded by their matches and
 * reordered by activity rather than appended to; the list is read as a whole,
 * from the top, and a page number is a useful thing to have. A *timeline* is the
 * case offsets get wrong (see `common/cursor-page`), and a chat list is not one.
 *
 * Two whitelisted filters (N7):
 *
 * - `status` — so a client can ask for the closed ones without a second route.
 *   The **default is decided by the service**, not here: a default in the schema
 *   would read as though closed conversations were unreachable, whereas the
 *   truth is that the endpoint chooses and a member may ask (the reasoning
 *   `MatchesService` states for the same shape).
 * - `matchId` — so the "it's a match" moment, a matches row and a member detail
 *   can each open the conversation they already hold a match for. Without it the
 *   client would need a route that resolves a match to a conversation, and that
 *   route would be an ownership check to forget. Here the filter runs *inside*
 *   the member's own scoped list, so it can only ever find a conversation they
 *   are already in.
 */
export const ConversationsListQuerySchema = makeListQuery({
  status: ConversationStatusSchema.optional(),
  matchId: UuidSchema.optional(),
});

export type ConversationsListQuery = z.infer<typeof ConversationsListQuerySchema>;
